import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { apiPlansIdFormatGetOptions } from "@/generated/api/@tanstack/react-query.gen.ts";
import ContainerInset from "@/components/container-inset.tsx";
import { PageErrorNoData } from "@/components/page-error.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import PageLayoutRoot, {
  PageLayoutContent,
  PageLayoutHeader,
} from "@/components/page-layout.tsx";
import PlanFormSkeleton from "@/app/(authenticated)/$merchantId/plans/_components/plan-form-skeleton.tsx";
import PlanForm from "@/app/(authenticated)/$merchantId/plans/_components/plan-form.tsx";
import { useMerchantProvider } from "@/app/(authenticated)/_providers/merchant-provider.tsx";

export const Route = createFileRoute("/(authenticated)/$merchantId/plans/$id/")(
  {
    component: RouteComponent,
  }
);

function RouteComponent() {
  const { id, merchantId } = Route.useParams();
  const { selectedMerchant } = useMerchantProvider();

  const planQuery = useQuery({
    ...apiPlansIdFormatGetOptions({
      headers: {
        "X-Merchant-Account-Id": selectedMerchant?.merchAccountId ?? "unknown",
      },
      path: { id },
    }),
    enabled: !!selectedMerchant?.merchAccountId,
  });

  if (planQuery.isPending) {
    return (
      <ContainerInset
        breadcrumbs={[
          {
            title: "Recurring Plans",
            href: `/${merchantId}/plans`,
          },
          {
            title: "Plan Detail",
            href: "",
          },
        ]}
      >
        <PageLayoutRoot>
          <PageLayoutHeader
            title="Edit Payment Plan"
            description="Update plan details and configuration"
            tag={<Skeleton className="h-5 w-16" />}
          />

          {/* Form Content */}
          <PageLayoutContent>
            <PlanFormSkeleton />
          </PageLayoutContent>
        </PageLayoutRoot>
      </ContainerInset>
    );
  }

  if (planQuery.error) return <PageErrorNoData />;

  if (!planQuery.data) return <PageErrorNoData />;

  return (
    <ContainerInset
      breadcrumbs={[
        {
          title: "Recurring Plans",
          href: `/${merchantId}/plans`,
        },
        {
          title: "Plan Detail",
          href: "",
        },
      ]}
    >
      <PageLayoutRoot>
        <PageLayoutHeader
          title="Edit Payment Plan"
          description="Update plan details and configuration"
          tag={`#${planQuery.data.planReferenceId}`}
        />

        {/* Form Content */}
        <PageLayoutContent>
          <PlanForm
            onUpdateSuccess={async () => {
              await planQuery.refetch();
            }}
            initPlan={{
              ...planQuery.data,
              surcharge: planQuery.data?.surcharge,
            }}
          />
        </PageLayoutContent>
      </PageLayoutRoot>
    </ContainerInset>
  );
}
